import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactResizeDetector from "react-resize-detector";

import styles from "./Tab.module.scss";
export interface TabContentProps {
    id: string;
    onClick?: (tab: TabData) => void;
    onActivated?: (tab: TabData) => void;
    onNoContent?: (isNoContent: boolean) => void;
    onShortTitleClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tab: TabData) => void;
    onLongTitleClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tab: TabData) => void;
}

export const MenuButton: React.FC<{
    open?: boolean;
    onClick?: (open: boolean) => {};
}> = ({ open, onClick }) => {
    const [_open, setOpen] = useState(false);

    useEffect(() => {
        open != null && setOpen(open);
    }, [open]);

    const _styles = {
        container: {},
        line: {},
        lineTop: {
            transform: _open ? "rotate(45deg)" : "none"
        },
        lineMiddle: {
            opacity: _open ? 0 : 1,
            transform: _open ? "translateX(-16px)" : "none"
        },
        lineBottom: {
            transform: _open ? "translateX(-1px) rotate(-45deg)" : "none"
        }
    };

    const _onCLick = useCallback(() => {
        setOpen(!_open);
        onClick && onClick(_open);
    }, [_open, onClick]);
    return (
        <div style={_styles.container} className={styles.hamburgerButton} onClick={_onCLick}>
            <div style={{ ..._styles.line, ..._styles.lineTop }} className={`${styles.line} ${styles.lineTop}`} />
            <div style={{ ..._styles.line, ..._styles.lineMiddle }} className={`${styles.line} ${styles.lineMiddle}`} />
            <div style={{ ..._styles.line, ..._styles.lineBottom }} className={`${styles.line} ${styles.lineBottom}`} />
        </div>
    );
};

export const TabContent: React.FC<TabContentProps> = ({ onNoContent, onActivated, children }) => {
    const node = useRef(null);

    useEffect(() => {
        if (node.current && node.current.childNodes && !node.current.childNodes.length) {
            node.current.classList.add("no-content");
            onNoContent && onNoContent(true);
        } else if (node.current && node.current.childNodes && node.current.childNodes.length) {
            node.current.classList.remove("no-content");
            onNoContent && onNoContent(false);
        }
    });

    const _children = React.Children.toArray(children);
    if (!_children?.length) return null;

    return (
        <div ref={node} className="tab-content">
            {children}
        </div>
    );
};

export type TabVariant = "blank" | "stretch" | "hamburger";
const DEFAULT_VARIANT: TabVariant = "stretch";
export interface TabData {
    id: string;
    title?: string | JSX.Element;
    shortTitle?: string | JSX.Element;
    className?: string;
}
export interface TabProps {
    className?: string;
    activeId?: string;
    variant?: TabVariant;
    tabData: TabData[];
}

export const HamburgerMenu: React.FC<{
    data: TabData[];
    active: TabData;
    onClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, data: TabData) => void;
}> = ({ data, onClick }) => {
    const ulRef = useRef<HTMLUListElement>();
    const [open, setOpen] = useState(false);
    const _onClick = useCallback(() => {
        setOpen(!open);
    }, [open]);

    const [maxHeight, setMaxHeight] = useState(undefined);
    const adjustMaxHeight = useCallback(() => {
        const lis = ulRef.current?.querySelectorAll("li");
        let totalHeight = 0;
        lis?.forEach(li => {
            totalHeight += li.clientHeight;
        });

        setMaxHeight(totalHeight);
    }, [setMaxHeight]);

    useEffect(() => {
        adjustMaxHeight();
    }, [adjustMaxHeight]);

    const menuContentHeight = useMemo(() => {
        if (!open) return 0;

        return maxHeight;
    }, [maxHeight, open]);

    return (
        <>
            <div className={styles.hamburgerMenu} onClick={_onClick}>
                <div>Menu</div>
                <MenuButton open={open} />
            </div>
            <div
                className={styles.menuContent}
                style={{
                    maxHeight: `${menuContentHeight}px`
                }}
            >
                <ul ref={ulRef}>
                    {data?.map((d, index) => {
                        return (
                            <li
                                key={index}
                                onClick={e => {
                                    onClick && onClick(e, d);
                                }}
                            >
                                <div>{d.title}</div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </>
    );
};

export const Tab: React.FC<TabProps> = ({ className, tabData, activeId, variant, children }) => {
    const ref = useRef(null);
    const [_activeId, setActiveId] = useState<string>(activeId);
    const [tab, setTab] = useState<{
        data: TabData[];
        active: TabData;
    }>(undefined);

    useEffect(() => {
        setActiveId(activeId);
    }, [activeId]);

    useEffect(() => {
        const activeTab = tabData.find(({ id }) => id === _activeId) ?? tabData[0];
        setTab({ data: tabData, active: activeTab });
    }, [tabData, _activeId]);

    const _adjustMinHeight = useCallback((height: number) => {
        if (ref.current) {
            if (height != null) {
                ref.current.style.height = `${height + 10}px`;
                ref.current.style.overflow = "hidden";
            }
        }
    }, []);

    const onResizeContent = useCallback(() => {
        if (ref.current) {
            const tabHeader = ref.current.querySelector(".tab-header");
            const tabContent = ref.current.querySelector(".tab-active-content");

            // const _height = ref.current.clientHeight;

            if (tabContent && tabHeader) {
                const chTabContent = tabContent.clientHeight;
                const chTabHeader = tabHeader.clientHeight;

                const _height = chTabContent + chTabHeader + 16;
                _adjustMinHeight(_height);
            }
        }
    }, [_adjustMinHeight]);

    // const adjustMinHeight = useMemo(() => {
    //     return _.throttle(_adjustMinHeight, 1000);
    // }, []);

    if (!tab || !tab.active) return null;

    const { data, active } = tab;
    const { id: activeTabId } = active;

    let _children = React.Children.toArray(children);

    const tabContent = _children.filter(d => (d as ReactElement)?.type === TabContent);
    if (!tabContent?.length) return null;

    const activeTabContent = tabContent.find(d => (d as ReactElement<TabContentProps>)?.props?.id === activeTabId);
    if (!activeTabContent) return null;

    const { onClick, onActivated, onLongTitleClick, onShortTitleClick } =
        (activeTabContent as ReactElement<TabContentProps>)?.props ?? {};

    const tabHeader =
        variant !== "hamburger" ? (
            <>
                {data?.map(({ id, className, title, shortTitle, ...rest }, index) => {
                    return (
                        <div
                            key={index}
                            // key={id}
                            className={`col tab-header-container ${id === activeTabId ? "tab-active" : ""} ${
                                className ?? ""
                            }`}
                        >
                            <div
                                className={`content`}
                                onClick={() => {
                                    onClick &&
                                        onClick({
                                            id,
                                            title,
                                            shortTitle,
                                            className,
                                            ...rest
                                        });

                                    !onClick && setActiveId(id);

                                    const _tabContent = tabContent.find(
                                        d => (d as ReactElement<TabContentProps>)?.props?.id === id
                                    );
                                    if (!_tabContent) return null;

                                    const { onActivated } = (_tabContent as ReactElement<TabContentProps>)?.props ?? {};

                                    onActivated &&
                                        onActivated({
                                            id,
                                            title,
                                            shortTitle,
                                            className,
                                            ...rest
                                        });
                                }}
                            >
                                <span
                                    className="long-tab-title"
                                    onClick={e => {
                                        onLongTitleClick &&
                                            onLongTitleClick(e, {
                                                id,
                                                title,
                                                shortTitle,
                                                className,
                                                ...rest
                                            });
                                    }}
                                >
                                    {title}
                                </span>
                                <span
                                    className="short-tab-title"
                                    onClick={e => {
                                        onShortTitleClick &&
                                            onShortTitleClick(e, {
                                                id,
                                                title,
                                                shortTitle,
                                                className,
                                                ...rest
                                            });
                                    }}
                                >
                                    {shortTitle}
                                </span>
                            </div>

                            <div className="bottom"></div>
                        </div>
                    );
                })}
            </>
        ) : (
            <HamburgerMenu
                active={active}
                data={data}
                onClick={(e, d) => {
                    onClick && onClick(d);
                    !onClick && setActiveId(d.id);
                    onActivated && onActivated(d);
                    onLongTitleClick && onLongTitleClick(e, d);
                }}
            />
        );

    return (
        <div className={`tab-control ${className ?? ""} ${styles?.[variant ?? DEFAULT_VARIANT] ?? ""}`} ref={ref}>
            <div className="d-flex tab-header">{tabHeader}</div>
            <div className="d-flex tab-header-bottom"></div>
            <div className="d-flex tab-active-content">
                <div className="col-12 tab-active-content-wrapper">{activeTabContent}</div>

                <ReactResizeDetector handleWidth={true} handleHeight={true} onResize={onResizeContent} />
            </div>
        </div>
    );
};

export const TabDemo = () => {
    const tabData = React.useMemo<TabData[]>(() => {
        return [
            {
                id: "aaa",
                title: "aaa",
                shortTitle: "aaa"
            },
            {
                id: "bbb",
                title: "bbb",
                shortTitle: "bbb"
            },
            {
                id: "ccc",
                title: "ccc",
                shortTitle: "ccc"
            }
        ];
    }, []);

    return (
        <Tab tabData={tabData} activeId="bbb">
            <TabContent id="aaa">
                <div>aaa</div>
            </TabContent>
            <TabContent id="bbb">
                <div>bbb</div>
            </TabContent>
            <TabContent id="ccc">
                <div>ccc</div>
            </TabContent>
        </Tab>
    );
};
